import React from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Shield, Lock, Eye, FileText, Mail } from 'lucide-react';

const PrivacyPolicyPage = () => {
  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      {/* Hero Section */}
      <section className="pt-32 pb-20 bg-black">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto text-center">
            <Shield className="w-16 h-16 text-green-600 mx-auto mb-6" />
            <h1 className="text-5xl sm:text-6xl font-bold text-white mb-6">
              Política de Privacidad
            </h1>
            <p className="text-xl text-gray-300 font-light">
              En Cloudbase, respetamos y protegemos la privacidad de todos nuestros usuarios
            </p>
          </div>
        </div>
      </section>

      {/* Content Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto prose prose-lg">
            
            {/* Introduction */}
            <div className="mb-16">
              <p className="text-xl text-gray-700 leading-relaxed mb-6">
                En <strong>Cloudbase SpA</strong> ("Cloudbase", "nosotros" o "nuestra empresa"), respetamos y protegemos la privacidad de todos los usuarios de nuestro <strong>ERP y aplicaciones móviles</strong>. Esto incluye tanto a nuestros <strong>clientes corporativos (coworks, empresas u organizaciones)</strong> como a los <strong>usuarios finales</strong> que hacen uso de los servicios a través de dichas plataformas.
              </p>
              <p className="text-xl text-gray-700 leading-relaxed">
                Al utilizar nuestros sistemas, confirmas que aceptas las prácticas descritas en la presente <strong>Política de Privacidad</strong>.
              </p>
            </div>

            {/* Section 1 */}
            <div className="mb-16 bg-gray-50 p-8 border-l-4 border-green-600">
              <h2 className="text-3xl font-bold text-gray-900 mb-6 flex items-center gap-3">
                <FileText className="w-8 h-8 text-green-600" />
                1. Alcance de esta Política
              </h2>
              <p className="text-gray-700 leading-relaxed mb-4">Esta política aplica a:</p>
              <ul className="space-y-3 text-gray-700">
                <li className="flex items-start gap-2">
                  <span className="text-green-600 font-bold">•</span>
                  <span>El <strong>ERP Cloudbase</strong> utilizado por nuestros clientes corporativos (B2B).</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-green-600 font-bold">•</span>
                  <span>La <strong>aplicación móvil vinculada al ERP</strong> utilizada por clientes finales para reservas, accesos, solicitudes de servicios u otros módulos que provea la plataforma.</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-green-600 font-bold">•</span>
                  <span>Los servicios de integración asociados (ej. sistemas de control de acceso, impresión o WiFi administrados).</span>
                </li>
              </ul>
            </div>

            {/* Section 2 */}
            <div className="mb-16">
              <h2 className="text-3xl font-bold text-gray-900 mb-6 flex items-center gap-3">
                <Eye className="w-8 h-8 text-green-600" />
                2. Información que recopilamos
              </h2>
              
              <h3 className="text-2xl font-bold text-gray-900 mt-8 mb-4">a) Información de los clientes corporativos (B2B)</h3>
              <ul className="space-y-2 text-gray-700 mb-6">
                <li>• Datos de la empresa (razón social, RUT/NIT, direcciones de facturación, contacto administrativo y comercial).</li>
                <li>• Usuarios designados como administradores del sistema.</li>
                <li>• Información de uso general del ERP (módulos contratados, reportes, configuración).</li>
              </ul>

              <h3 className="text-2xl font-bold text-gray-900 mt-8 mb-4">b) Información de los usuarios finales</h3>
              <ul className="space-y-2 text-gray-700">
                <li>• <strong>Datos de identificación</strong>: nombre, correo electrónico, número de documento, teléfono (cuando sea requerido).</li>
                <li>• <strong>Datos de uso del servicio</strong>: reservas de salas, accesos registrados, uso de impresoras u otros recursos del cowork.</li>
                <li>• <strong>Datos técnicos</strong>: dirección IP, dispositivo utilizado, sistema operativo, logs de conexión y eventos en la aplicación.</li>
                <li>• <strong>Datos opcionales</strong>: información de ubicación (solo si la función lo requiere y el usuario lo autoriza).</li>
              </ul>
            </div>

            {/* Section 3 */}
            <div className="mb-16 bg-gray-50 p-8 border-l-4 border-green-600">
              <h2 className="text-3xl font-bold text-gray-900 mb-6">3. Finalidad del uso de la información</h2>
              <p className="text-gray-700 mb-4">La información recopilada se utiliza para:</p>
              <ol className="space-y-3 text-gray-700">
                <li><strong>1. Provisión del servicio</strong>: gestión de reservas, accesos, facturación y uso de módulos contratados por cada cowork o empresa.</li>
                <li><strong>2. Mejora continua</strong>: análisis de uso para optimizar el rendimiento de la aplicación y agregar nuevas funcionalidades.</li>
                <li><strong>3. Seguridad</strong>: validación de identidad, trazabilidad de accesos y prevención de usos indebidos.</li>
                <li><strong>4. Cumplimiento legal</strong>: dar respuesta a solicitudes de autoridades competentes o requerimientos legales.</li>
                <li><strong>5. Comunicación</strong>: envío de notificaciones relacionadas al servicio, recordatorios de reservas, accesos o comunicaciones administrativas.</li>
              </ol>
            </div>

            {/* Section 4 */}
            <div className="mb-16">
              <h2 className="text-3xl font-bold text-gray-900 mb-6 flex items-center gap-3">
                <Lock className="w-8 h-8 text-green-600" />
                4. Compartición de la información
              </h2>
              <ul className="space-y-4 text-gray-700">
                <li>• <strong>Con el cliente B2B</strong>: los datos de los usuarios finales son visibles únicamente para la empresa o cowork que contrató el servicio, en el marco de su propia operación.</li>
                <li>• <strong>Con terceros proveedores</strong>: podemos compartir datos limitados y necesarios con proveedores tecnológicos (ej. hosting en la nube, pasarelas de pago, control de accesos físicos), bajo estrictos contratos de confidencialidad y cumplimiento normativo.</li>
                <li>• <strong>Obligaciones legales</strong>: podremos compartir información si es requerido por una autoridad competente, conforme a la ley aplicable.</li>
              </ul>
              <p className="text-gray-700 mt-6 font-semibold">
                En ningún caso se venden, arrientan ni comercian datos personales.
              </p>
            </div>

            {/* Section 5 */}
            <div className="mb-16 bg-gray-50 p-8 border-l-4 border-green-600">
              <h2 className="text-3xl font-bold text-gray-900 mb-6">5. Conservación y protección de la información</h2>
              <ul className="space-y-3 text-gray-700">
                <li>• Los datos se almacenan en servidores seguros, ubicados en centros de datos que cumplen con estándares internacionales de seguridad.</li>
                <li>• Implementamos medidas técnicas, administrativas y organizativas razonables para prevenir accesos no autorizados, alteración o destrucción de la información.</li>
                <li>• La información se conserva mientras sea necesaria para cumplir con las finalidades descritas o según los plazos que determine la normativa vigente.</li>
              </ul>
            </div>

            {/* Section 6 */}
            <div className="mb-16">
              <h2 className="text-3xl font-bold text-gray-900 mb-6">6. Derechos de los usuarios</h2>
              <p className="text-gray-700 mb-4">Los usuarios (tanto empresas como clientes finales) pueden solicitar en cualquier momento:</p>
              <ul className="space-y-2 text-gray-700">
                <li>• Acceso a sus datos personales.</li>
                <li>• Rectificación de información inexacta o desactualizada.</li>
                <li>• Eliminación de sus datos, en los casos en que proceda legalmente.</li>
                <li>• Limitación del tratamiento o portabilidad de datos, según corresponda.</li>
              </ul>
              <p className="text-gray-700 mt-4">
                Las solicitudes se pueden realizar escribiendo a nuestro correo de contacto.
              </p>
            </div>

            {/* Section 7 */}
            <div className="mb-16 bg-gray-50 p-8 border-l-4 border-green-600">
              <h2 className="text-3xl font-bold text-gray-900 mb-6">7. Cambios en esta política</h2>
              <p className="text-gray-700 mb-4">
                Cloudbase se reserva el derecho de actualizar esta política para reflejar cambios en nuestros servicios, obligaciones legales o mejoras en nuestras prácticas de privacidad.
              </p>
              <p className="text-gray-700">
                Se notificará a los clientes y usuarios mediante correo electrónico o notificación dentro de la aplicación cuando existan cambios relevantes.
              </p>
            </div>

            {/* Section 8 - Contact */}
            <div className="bg-black text-white p-12 text-center">
              <Mail className="w-12 h-12 text-green-500 mx-auto mb-6" />
              <h2 className="text-3xl font-bold mb-6">8. Contacto</h2>
              <p className="text-xl mb-6">
                Si tienes dudas, solicitudes o reclamos respecto a esta política de privacidad, puedes contactarnos en:
              </p>
              <a 
                href="mailto:contacto@cloudbase.cl" 
                className="text-2xl font-bold text-green-500 hover:text-green-400 transition-colors"
              >
                contacto@cloudbase.cl
              </a>
            </div>

          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default PrivacyPolicyPage;
